<template>
  <div>
    <div class="form-avatar">
      <UpdateAvatar v-if="asyncData" :image="image" @crop-image="cropImage" />
    </div>
    <div class="form-avatar-skeleton" v-if="!asyncData">
      <ion-list class="ion-no-padding" lines="none">
        <ion-avatar slot="start">
          <ion-skeleton-text animated></ion-skeleton-text>
        </ion-avatar>
        <ion-skeleton-text class="skeleton-text" animated style="width: 100%"></ion-skeleton-text>
      </ion-list>
    </div>
    <div class="form-group">
      <ion-list class="ion-no-padding" lines="none">
        <ion-item
          :class="
            form.firstName.isError ? 'item-danger' : form.firstName.value ? 'item-primary' : 'item-medium'
          "
        >
          <div class="form-control">
            <ion-label
              :class="form.firstName.isError ? 'text-danger' : form.firstName.value ? 'text-primary' : ''"
              >{{ $t('first_name') }}</ion-label
            >
            <ion-input
              v-model="form.firstName.value"
              @ionInput="handleValidateForm('firstName')"
              class="input"
            />
          </div>
        </ion-item>
      </ion-list>
      <ion-text>
        <div v-if="form.firstName.isError" class="py-1 fs-12px text-danger form-error">
          {{
            form.firstName.value?.length === 0
              ? $t('first_name_cannot_be_empty')
              : $t('first_name_must_be_at_least_2_characters')
          }}
        </div>
      </ion-text>
    </div>
    <div class="form-group">
      <ion-list
        :class="['ion-no-padding', form.lastName.value ? 'item-primary' : 'item-medium']"
        lines="none"
      >
        <ion-item>
          <div class="form-control">
            <ion-label :class="form.lastName.value ? 'text-primary' : ''">{{ $t('last_name') }}</ion-label>
            <ion-input v-model="form.lastName.value" class="input" />
          </div>
        </ion-item>
      </ion-list>
    </div>
    <div class="form-group">
      <ion-list
        :class="['ion-no-padding', isValidDate(form.birthDate.value) ? 'item-primary' : 'item-medium']"
        lines="none"
      >
        <ion-item @click="onShowModalDateTime">
          <div class="form-control">
            <ion-label :class="form.birthDate.value ? 'text-primary' : ''">{{ $t('birth_date') }}</ion-label>
            <ion-text>{{
              isValidDate(form.birthDate.value) ? formatDateMonthYear(form.birthDate.value) : '-'
            }}</ion-text>
            <ModalDateTime
              v-if="isShowModalDateTime"
              :initialization_time="isValidDate(form.birthDate.value) ? form.birthDate.value : null"
              @handleSendTimeChange="handleSendTimeChange"
            />
          </div>
        </ion-item>
      </ion-list>
    </div>
    <div class="form-group">
      <div class="form-group-radio">
        <ion-label :class="form.gender.value ? 'text-primary' : ''">{{ $t('gender') }}</ion-label>
        <ion-list lines="none" mode="md">
          <ion-radio-group v-model="form.gender.value">
            <div class="d-flex">
              <ion-item class="ion-no-border">
                <ion-label>{{ $t('male') }}</ion-label>
                <ion-radio slot="start" value="m"></ion-radio>
              </ion-item>

              <ion-item class="ion-no-border ml-6">
                <ion-label>{{ $t('female') }}</ion-label>
                <ion-radio slot="start" value="f"></ion-radio>
              </ion-item>
            </div>
          </ion-radio-group>
        </ion-list>
      </div>
    </div>

    <div class="form-group">
      <div
        :class="[
          'form-phone',
          form.mobile.isError ? 'item-danger' : form.mobile.value ? 'item-primary' : 'item-medium'
        ]"
      >
        <div class="phone-input">
          <ion-label :class="form.mobile.isError ? 'text-danger' : form.mobile.value ? 'text-primary' : ''">
            {{ $t('phone') }}
          </ion-label>
          <vue-tel-input
            v-model="form.mobile.value"
            mode="national"
            enabledCountryCode
            enabledFlags
            validCharactersOnly
            pattern="[0-9]*$"
            styleClasses="no-border	no-box-shadow mt-2"
            ref="phoneField"
            inputId="phoneField"
            disabledFetchingCountry
            :onlyCountries="getPreferedCountryCode()"
            :autoDefaultCountry="false"
            :defaultCountry="defaultCountry"
            :autoFormat="false"
            @on-input="onlyNumber"
            @keypress="blockDecimalInput"
            :inputOptions="{
              type: 'tel',
              maxlength: 16,
              placeholder: $t('mobile_number')
            }"
            @country-changed="countryChange"
          />
        </div>
      </div>
      <ion-text>
        <div v-if="form.mobile.isError" class="py-1 fs-12px text-danger form-error">
          {{ $t('phone_number_cannot_be_empty') }}
        </div>
      </ion-text>
    </div>
    <div class="form-group">
      <ion-list class="ion-no-padding" lines="none">
        <ion-item
          :class="form.email.isError ? 'item-danger' : form.email.value ? 'item-primary' : 'item-medium'"
        >
          <div class="form-control">
            <ion-label :class="form.email.isError ? 'text-danger' : form.email.value ? 'text-primary' : ''">{{
              $t('email')
            }}</ion-label>
            <ion-input v-model="form.email.value" @ionInput="handleValidateForm('email')" class="input" />
          </div>
        </ion-item>
      </ion-list>
      <ion-text>
        <div v-if="form.email.isError" class="py-1 fs-12px text-danger form-error">
          {{
            form.email.value?.length === 0
              ? $t('updateProfile.email_cannot_be_empty')
              : $t('this_field_must_be_a_valid_email')
          }}
        </div>
      </ion-text>
    </div>
    <div class="form-btn">
      <ion-button color="primary" mode="ios" @click="handleSaveChanges">{{ $t('save_changes') }}</ion-button>
    </div>
    <ion-button class="mb-3" color="danger" fill="clear" expand="block" @click="setOpenModalDelete(true)">
      <ion-icon slot="start" :icon="warningOutline"></ion-icon>
      {{ $t('delete_account_button') }}</ion-button
    >
    <ion-modal :is-open="onShowOtpVerification" mode="ios">
      <VerifyPhoneNumber
        @closeModal="onCloseOtpVerification"
        @handleResentOtp="onReSendOtp"
        @handleErrorEmit="handleErrorEmit"
        @handleOpenLoading="handleOpenLoading"
        @handleSuccessVerify="onVerifyOtpCode"
        @otpSuspend="isOtpSuspend = true"
        :countryCode="countryCode"
        :phone="form.mobile.value"
        :image="image"
        :oldImage="oldImage"
        :gender="form.gender.value"
        :email="form.email.value"
        :lastName="form.lastName.value"
        :firstName="form.firstName.value"
        :tenant_id="form.tenant_id.value"
      />
    </ion-modal>
    <ion-modal :is-open="isOtpSuspend">
      <otp-suspend @cancelOtp="isOtpSuspend = false" />
    </ion-modal>
    <ion-loading
      mode="ios"
      :is-open="isOpenRef"
      cssClass="my-custom-class"
      :message="`${$t('please_wait')}. . .`"
      @didDismiss="setOpen(false)"
    >
    </ion-loading>
    <ion-toast
      mode="ios"
      color="dark"
      :is-open="isOpenRefToast"
      :message="$t('saved_successfully')"
      :duration="2000"
      position="top"
      @didDismiss="setOpenToast(false)"
    >
    </ion-toast>
    <ion-toast
      mode="ios"
      color="dark"
      :is-open="isOpenRefToast1"
      message="Verification Code Sent Successfully"
      :duration="2000"
      position="top"
      @didDismiss="setOpenToast1(false)"
    >
    </ion-toast>
    <!-- modal delete account -->
    <ion-modal
      :is-open="isOpenModalDeleteRef"
      css-class="modal-confirm custom-modal"
      @didDismiss="setOpenModalDelete(false)"
    >
      <modal-content-confirm
        :title="$t('delete_account_title')"
        :content="$t('delete_account_confirmation')"
        :yesLabel="$t('delete_account_agree')"
        :noLabel="$t('delete_account_go_back')"
        @confirm-yes-action="deleteAccount"
        @confirm-no-action="setOpenModalDelete(false)"
      >
      </modal-content-confirm>
    </ion-modal>
    <ion-loading
      :is-open="isOpenLoadingref"
      @didDismiss="setOpenLoading(false)"
      cssClass="custom-loading"
      message=""
      spinner="crescent"
    >
    </ion-loading>
  </div>
</template>

<script>
import ModalDateTime from '@/components/DateTime/index.vue';
import OtpSuspend from '@/components/OtpSuspend';
import { apolloClient } from '@/main';
import { checkConnectPerson, updateProfile } from '@/modules/b2b/services/graphql';
import {
  findObjectByKey,
  getDefaultCountryCode,
  getPreferedCountryCode,
  getRegexEmail
} from '@/modules/b2b/services/libs/helper';
import { ACTIONS as ACCOUNT_ACTIONS } from '@/modules/b2b/store/account/actions';
import ModalContentConfirm from '@/modules/b2b/views/shared/ModalContentConfirm.vue';
import { useAlert } from '@/modules/shared/utils';
import { checkUser, getCurrentUser } from '@/services/shared/graphql';
import { VERIFICATION_TYPE } from '@/services/shared/helper/constants';
import { useDateFormatter } from '@/usecases/global';
import { alertController } from '@ionic/vue';
import { warningOutline } from 'ionicons/icons';
import debounce from 'lodash.debounce';
import { defineComponent, inject, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';
import { createNamespacedHelpers, useStore } from 'vuex';
import UpdateAvatar from './UpdateAvatar.vue';
import VerifyPhoneNumber from './VerifyPhoneNumber.vue';

const { mapActions } = createNamespacedHelpers('b2b/account');

export default defineComponent({
  components: {
    UpdateAvatar,
    VueTelInput,
    VerifyPhoneNumber,
    ModalDateTime,
    OtpSuspend,
    ModalContentConfirm
  },
  setup() {
    const $storage = inject('$storage');
    const { formatDateMonthYear, isValidDate } = useDateFormatter();
    const store = useStore();
    const { t } = useI18n();
    const router = useRouter();
    const { createAlert } = useAlert();
    const isOpenRef = ref(false);
    const isOpenRefToast = ref(false);
    const isOpenRefToast1 = ref(false);
    const isShowModalDateTime = ref(false);
    const isOtpSuspend = ref(false);
    const isOpenLoadingref = ref(false);
    const isOpenModalDeleteRef = ref(false);
    const asyncData = ref(false);
    const oldPhoneNumber = ref(``);
    const oldImage = ref('');
    const image = ref(``);
    const countryCode = ref('');
    const defaultCountry = ref('');
    const tenantId = ref(``);
    const userId = ref(``);
    const phoneField = ref(null);
    const form = ref({
      firstName: {
        value: '',
        isError: false
      },
      mobile: {
        value: '',
        isError: false
      },
      email: {
        value: '',
        isError: false
      },
      lastName: {
        value: ''
      },
      gender: {
        value: ''
      },
      birthDate: {
        value: ''
      },
      tenant_id: {
        value: ''
      }
    });
    const setOpen = async (state) => (isOpenRef.value = state);
    const setOpenToast = async (state) => (isOpenRefToast.value = state);
    const setOpenToast1 = async (state) => (isOpenRefToast1.value = state);
    const setOpenLoading = (state) => (isOpenLoadingref.value = state);
    const setOpenModalDelete = (state) => (isOpenModalDeleteRef.value = state);
    const fetchLoggedInUser = async () => {
      try {
        const { data } = await apolloClient.query({
          query: getCurrentUser
        });
        const profile = data.me;
        tenantId.value = profile.tenant.id;
        userId.value = profile.id;
        image.value = profile.image || null;
        form.value.firstName.value = profile.first_name || '-';
        form.value.lastName.value = profile.last_name || '-';
        form.value.gender.value = profile.gender || 'o';
        form.value.mobile.value = profile.mobile || '-';
        form.value.birthDate.value = profile.birth_date || '-';
        form.value.tenant_id.value = profile.tenant.id;
        form.value.email.value = profile.email || '-';
        asyncData.value = true;
        oldPhoneNumber.value = form.value.mobile.value || '-';
        const fullPhoneNumber = form.value.mobile.value;
        form.value.mobile.value =
          fullPhoneNumber.indexOf(' ') > -1
            ? fullPhoneNumber.split(' ')[1]
            : fullPhoneNumber.length > 8
            ? fullPhoneNumber.substring(2, fullPhoneNumber.length)
            : fullPhoneNumber;
        nextTick(() => {
          splitPhoneAndCountryCode(fullPhoneNumber, form.value.mobile.value);
        });
      } catch (e) {
        createAlert(t('errorTitle'), e.message);
      }
    };
    const splitPhoneAndCountryCode = (fullPhoneNumber, phone) => {
      countryCode.value =
        fullPhoneNumber.indexOf(' ') > -1
          ? fullPhoneNumber.split(' ')[0]
          : fullPhoneNumber.length > 8
          ? fullPhoneNumber.substring(0, 2)
          : getDefaultCountryCode();
      const country = findObjectByKey(phoneField.value.allCountries, 'dialCode', countryCode.value);
      defaultCountry.value = country ? country.iso2 : null;
      phoneField.value.choose(defaultCountry.value);
      form.value.mobile.value = phone;
    };
    const deleteAccount = async () => {
      setOpenLoading(true);
      await store.dispatch(`b2b/account/${ACCOUNT_ACTIONS.DELETE_ACCOUNT}`);
      setOpenModalDelete(false);
      setOpenLoading(false);
      await $storage.clearStorage();
      router.replace('/');
    };
    const countryChange = (val) => {
      defaultCountry.value = val.iso2;
      countryCode.value = val.dialCode || getDefaultCountryCode();
    };
    const onShowModalDateTime = () => {
      isShowModalDateTime.value = false;
      nextTick(() => {
        isShowModalDateTime.value = true;
      });
    };
    const cropImage = (cropImg) => {
      image.value = cropImg;
      oldImage.value = cropImg;
    };
    const onlyNumber = (val) => {
      const input = val
        .toString()
        .replace(/^0+|^,|^\.+/g, '')
        .replace(/[^\d.,]/g, '');
      form.value.mobile.value = input.replace(/[.,]/g, '');
    };
    const blockDecimalInput = (event) => {
      let keyCode = event.keyCode ? event.keyCode : event.which;
      if (keyCode < 48 || keyCode > 57) {
        event.preventDefault();
      }
    };
    onMounted(() => {
      fetchLoggedInUser();
    });
    return {
      onShowOtpVerification: ref(false),
      formatDateMonthYear,
      isValidDate,
      warningOutline,
      getPreferedCountryCode,
      isOpenRef,
      setOpen,
      isOpenRefToast,
      setOpenToast,
      isOpenRefToast1,
      setOpenToast1,
      form,
      isShowModalDateTime,
      onShowModalDateTime,
      isOtpSuspend,
      isOpenModalDeleteRef,
      setOpenModalDelete,
      isOpenLoadingref,
      setOpenLoading,
      router,
      deleteAccount,
      defaultCountry,
      countryCode,
      asyncData,
      userId,
      oldPhoneNumber,
      oldImage,
      image,
      tenantId,
      countryChange,
      phoneField,
      createAlert,
      cropImage,
      onlyNumber,
      blockDecimalInput
    };
  },
  methods: {
    ...mapActions([ACCOUNT_ACTIONS.GET_CURRENT_USER]),
    validateForm(params) {
      try {
        if (params === 'firstName') {
          const lengthFirstName = this.form.firstName.value?.length || 0;
          this.form.firstName.isError = lengthFirstName === 0 || lengthFirstName < 2;
        } else if (params === 'phone') {
          this.form.mobile.isError = this.form.mobile.value?.length === 0;
        } else {
          const emailValue = this.form.email?.value;
          if (!emailValue || !getRegexEmail().test(emailValue)) {
            this.form.email.isError = true;
          } else {
            this.form.email.isError = false;
          }
        }
      } catch (e) {
        this.createAlert(this.$t('errorTitle'), e);
      }
    },
    handleValidateForm: debounce(async function (params) {
      this.validateForm(params);
    }, 500),
    async handleSaveChanges() {
      const data = ['firstName', 'phone', 'email'];
      for (let value of data) {
        this.validateForm(value);
      }
      let isUpdate = true;
      for (let i in this.form) {
        if (this.form[i].isError === true) {
          isUpdate = false;
          break;
        }
      }
      if (isUpdate) {
        try {
          if (`${this.countryCode}${this.form.mobile.value}` === this.oldPhoneNumber) {
            this.handleUpdateProfile();
          } else {
            await this.setOpen(true);
            const { data } = await apolloClient.query({
              query: checkUser,
              variables: {
                mobile: `${this.countryCode + this.form.mobile.value}`
              }
            });

            if (data.checkUser === null) {
              await this.setOpen(false);
              this.onShowOtpVerification = true;
            } else {
              await this.setOpen(false);
              const message = this.$t('check_phone_number');
              this.createAlert(this.$t('errorTitle'), message);
            }
          }
        } catch (e) {
          if (e.message.includes('too many')) {
            this.isOtpSuspend = true;
            await this.setOpen(false);
          } else {
            await this.setOpen(false);
            const message = this.$t('check_phone_number_error');
            this.createAlert(this.$t('errorTitle'), message);
          }
        }
      }
    },
    handleUpdateProfile: debounce(async function () {
      try {
        await this.setOpen(true);
        const items = {
          firstName: this.form.firstName.value,
          lastName: this.form.lastName.value,
          gender: this.form.gender.value,
          email: this.form.email.value,
          birthDate: this.form.birthDate.value,
          tenantId: this.tenantId,
          ...(this.oldImage ? { image: this.image } : {})
        };
        const { data } = await apolloClient.query({
          query: updateProfile,
          variables: items
        });
        await this[ACCOUNT_ACTIONS.GET_CURRENT_USER]();
        await this.setOpen(false);
        await this.setOpenToast(true);
        setTimeout(() => {
          if (data.updateUser) {
            this.$router.push({
              path: '/b2b/account',
              query: { isRenderProfile: true }
            });
          } else {
            this.$router.push('/b2b/account');
          }
        }, 500);
      } catch (error) {
        if (error.message.includes('too many')) {
          this.isOtpSuspend.value = true;
          await this.setOpen(false);
        } else {
          await this.setOpen(false);
          this.createAlert(this.$t('errorTitle'), error.message);
        }
      }
    }, 500),
    async handleGetConnectPerson() {
      try {
        const { data } = await apolloClient.query({
          query: checkConnectPerson,
          variables: {
            userId: this.userId
          }
        });
        if (data.checkConnectPerson?.length === 1) {
          const info = data.checkConnectPerson[0]?.sale_user_name || 'Admin';
          const message = `${
            info +
            ' ' +
            this.$t('from') +
            data.checkConnectPerson[0]?.supplier_name +
            ' ' +
            this.$t('addCompany.would_you_like') +
            ' ' +
            data.checkConnectPerson[0]?.customer_name
          }`;
          this.alertCheckKeyContact(message);
        } else if (data.checkConnectPerson?.length > 1) {
          const message =
            this.$t('addCompany.there_are') +
            ' ' +
            data.checkConnectPerson.length +
            ' ' +
            this.$t('addCompany.companies_that_assigned');
          this.alertCheckKeyContact(message);
        }
      } catch (error) {
        this.createAlert(this.$t('errorTitle'), error.message);
      }
    },
    async alertCheckKeyContact(message) {
      const alert = await alertController.create({
        mode: 'ios',
        cssClass: 'my-custom-class',
        header: this.$t('new_request'),
        message: message,
        buttons: [
          {
            text: this.$t('dismiss'),
            role: 'dismiss',
            cssClass: 'secondary',
            id: 'cancel-button'
          },
          {
            text: this.$t('view_details'),
            id: 'confirm-button',
            handler: () => {
              this.$router.push('/b2b/customers');
            }
          }
        ]
      });
      await alert.present();
    },
    onCloseOtpVerification() {
      this.onShowOtpVerification = false;
    },
    async onReSendOtp() {
      try {
        await this.setOpen(true);
        const { errors } = await apolloClient.query({
          query: checkUser,
          variables: {
            mobile: `${this.countryCode + this.form.mobile.value}`,
            verificationType: VERIFICATION_TYPE.MOBILE_OTP
          }
        });
        if (errors) {
          await this.setOpen(false);
          const message = errors[0].message;
          this.createAlert(this.$t('errorTitle'), message);
        } else {
          await this.setOpen(false);
          await this.setOpenToast1(true);
        }
      } catch (e) {
        this.createAlert(this.$t('errorTitle'), e.message);
      }
    },

    handleErrorEmit(message) {
      this.createAlert(this.$t('errorTitle'), message);
    },
    handleOpenLoading(params) {
      this.setOpen(params);
    },
    async onVerifyOtpCode() {
      this.onShowOtpVerification = false;
      await this.setOpenToast(true);
      this.handleGetConnectPerson();
      setTimeout(() => {
        this.$router.push('/b2b/account');
      }, 500);
    },
    handleSendTimeChange(e) {
      this.form.birthDate.value = String(e);
    }
  },
  watch: {
    'form.mobile.value': debounce(function () {
      this.validateForm('phone');
    }, 500)
  }
});
</script>

<style src="../styles/UpdateProfile.scss" lang="scss" scoped></style>
